// ** react imports
import { useMemo } from 'react';

// ** routers imports
import { Routes, Route, Navigate } from 'react-router-dom';

// ** redux imports
import { useDispatch } from 'react-redux';
import { setUserDetails } from '../redux/slices/users';

// ** layout imports
import MainLayout from '../layouts/Main';
import AuthLayout from '../layouts/Auth';
import BlankLayout from '../layouts/BlankLayout';

// ** pages imports
import Signin from '../pages/Signin';
import ForgotPassword from '../pages/ForgotPassword';
import SetNewPassword from '../pages/SetNewPassword';
import PageNotFound from '../pages/utility/PageNotFound';
import UserList from '../pages/users/list';
import AddUser from '../pages/users/add';
import UsersDetails from '../pages/users/details';
import CustomerList from '../pages/customer/list';
import CustomerDetails from '../pages/customer/details';
import ListQuiz from '../pages/quiz/list';
import AddQuiz from '../pages/quiz/add';
import UpdateQuiz from '../pages/quiz/update';
import AddTaxFile from '../pages/taxFile/add';
import TaxFileList from '../pages/taxFile/list';
import TaxFileDetails from '../pages/taxFile/details';
import Appointment from '../pages/appointment';
import MobileAppVersionList from '../pages/mobileAppVersion/list';
import AddMobileAppVersion from '../pages/mobileAppVersion/add';
import UpdateMobileAppVersion from '../pages/mobileAppVersion/update';
import AnnouncementList from '../pages/announcement/list';
import AnnouncementAdd from '../pages/announcement/add';
import AnnouncementUpdate from '../pages/announcement/update';
import NotificationList from '../pages/notification/list';
import NotificationAdd from '../pages/notification/add';
import NotificationUpdate from '../pages/notification/update';
import AccountDelete from '../pages/accountDelete';
import PrivacyPolicy from '../pages/privacyPolicy';
import TermsAndConditions from '../pages/termsAndConditions';

// ** protected route imports
import ProtectedRoute from './ProtectedRoute';
import ManagerProtectedRoute from './ProtectedRoute/ManagerProtectedRoute';

// ** unprotected route imports
import UnprotectedRoute from './UnprotectedRoute';

// ** enums imports
import { USER_ROLES } from '../enums/common';

// ** utils imports
import SessionStorage from '../utils/SessionStorage';

const ProtectedRoutes = [
  {
    path: '/',
    element: <Navigate to="/users/list" />,
  },
  {
    path: '/users',
    children: [
      {
        path: 'list',
        element: <UserList />,
      },
      {
        path: 'add',
        element: (
          <ManagerProtectedRoute>
            <AddUser />
          </ManagerProtectedRoute>
        ),
      },
      {
        path: 'update/:id',
        element: (
          <ManagerProtectedRoute>
            <AddUser />
          </ManagerProtectedRoute>
        ),
      },
      {
        path: 'details/:id',
        element: <UsersDetails />,
      },
    ],
  },
  {
    path: '/customers',
    children: [
      {
        path: 'list',
        element: <CustomerList />,
      },
      {
        path: 'details/:id',
        element: <CustomerDetails />,
      },
    ],
  },
  {
    path: '/quiz',
    children: [
      {
        path: 'list',
        element: (
          <ManagerProtectedRoute>
            <ListQuiz />
          </ManagerProtectedRoute>
        ),
      },
      {
        path: 'add',
        element: (
          <ManagerProtectedRoute>
            <AddQuiz />
          </ManagerProtectedRoute>
        ),
      },
      {
        path: 'update/:id',
        element: (
          <ManagerProtectedRoute>
            <UpdateQuiz />
          </ManagerProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '/tax-files',
    children: [
      {
        path: 'list',
        element: <TaxFileList />,
      },
      {
        path: 'add',
        element: <AddTaxFile />,
      },
      {
        path: 'details/:taxFileId',
        element: <TaxFileDetails />,
      },
    ],
  },
  {
    path: '/appointment',
    element: <Appointment />,
  },
  {
    path: '/mobile-app-version',
    children: [
      {
        path: 'list',
        element: <MobileAppVersionList />,
      },
      {
        path: 'add',
        element: <AddMobileAppVersion />,
      },
      {
        path: 'update/:mobileVersionId',
        element: <UpdateMobileAppVersion />,
      },
    ],
  },
  {
    path: '/announcements',
    children: [
      {
        path: 'list',
        element: <AnnouncementList />,
      },
      {
        path: 'add',
        element: <AnnouncementAdd />,
      },
      {
        path: 'update/:id',
        element: <AnnouncementUpdate />,
      },
    ],
  },
  {
    path: '/notifications',
    children: [
      {
        path: 'list',
        element: <NotificationList />,
      },
      {
        path: 'add',
        element: <NotificationAdd />,
      },
      {
        path: 'update/:notificationId',
        element: <NotificationUpdate />,
      },
    ],
  },
];

const AuthRoutes = [
  {
    path: '/auth/signin',
    element: <Signin />,
  },
  {
    path: '/auth/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/auth/set-new-password',
    element: <SetNewPassword />,
  },
];

// ** blank routes imports - for routes that don't need any layout
const BlankRoutes = [
  {
    path: '/account-deletion',
    element: <AccountDelete />,
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms-and-conditions',
    element: <TermsAndConditions />,
  },
];

const AppRoutes = () => {
  // Vars
  const token = SessionStorage.get('token');
  const users = JSON.parse(SessionStorage.get('users') || '{}');

  // hooks
  const dispatch = useDispatch();

  useMemo(() => {
    if (token && !users.role) {
      // Redirect to the appropriate page based on user role
      SessionStorage.remove('token');
      SessionStorage.remove('users');
      window.location.href = '/auth/signin';
    }

    if (Object.keys(users).length === 0 && token) {
      dispatch(setUserDetails(users));
    }
  }, [dispatch, token, users]);

  return (
    <Routes>
      {/* Protected Routes */}
      <Route element={<MainLayout />}>
        {ProtectedRoutes.map(({ path, element: Component, children }) => {
          if (children) {
            return (
              <Route key={path} path={path}>
                {children.map(({ path: childPath, element: Child }) => (
                  <Route
                    key={childPath}
                    path={childPath}
                    element={<ProtectedRoute>{Child}</ProtectedRoute>}
                  />
                ))}
              </Route>
            );
          }

          return (
            <Route
              key={path}
              path={path}
              element={<ProtectedRoute>{Component}</ProtectedRoute>}
            />
          );
        })}
      </Route>

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        {AuthRoutes.map(({ path, element }, index) => (
          <Route
            key={index}
            path={path}
            element={<UnprotectedRoute>{element}</UnprotectedRoute>}
          />
        ))}
      </Route>

      {/* Error Routes */}
      {/* <Route element={<ErrorLayout />}>
        {ErrorRoutes.map(({ path, element }, index) => (
          <Route key={index} path={path} element={element} />
        ))}
      </Route> */}

      {/* Blank Routes */}
      <Route element={<BlankLayout />}>
        {BlankRoutes.map(({ path, element }, index) => (
          <Route key={index} path={path} element={element} />
        ))}
      </Route>

      {/* Fallback Route */}
      {token && users.role === USER_ROLES.ADMIN ? (
        <Route path="/" element={<Navigate to="/users/list" />} />
      ) : token && users.role === USER_ROLES.MANAGER ? (
        <Route path="/" element={<Navigate to="/customers/list" />} />
      ) : (
        <Route path="/" element={<Navigate to="/auth/signin" />} />
      )}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default AppRoutes;
